import React, { useState } from 'react';
import { MapPin, Phone, Mail, Clock, Send } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';
import usePageTitle from '../hooks/usePageTitle';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Contact = () => {
  usePageTitle('Contact Us');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    productType: '',
    quantity: '',
    message: ''
  });

  const [quoteData, setQuoteData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    product: '',
    quantity: '',
    deliveryLocation: '',
    requirements: ''
  });

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(`${API_URL}/api/contacts`, formData);
      console.log('Contact Form Submitted:', response.data);
      toast.success('Message sent successfully! We will contact you soon.');
      setFormData({
        name: '',
        email: '',
        phone: '',
        company: '',
        productType: '',
        quantity: '',
        message: ''
      });
    } catch (error) {
      console.error('Error submitting contact form:', error);
      toast.error('Failed to send message. Please try again.');
    }
  };

  const handleQuoteSubmit = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(`${API_URL}/api/quotes`, quoteData);
      console.log('Quote Request Submitted:', response.data);
      toast.success('Quote request received! Our team will send you a quote within 24 hours.');
      setQuoteData({
        name: '',
        email: '',
        phone: '',
        company: '',
        product: '',
        quantity: '',
        deliveryLocation: '',
        requirements: ''
      });
    } catch (error) {
      console.error('Error submitting quote request:', error);
      toast.error('Failed to submit quote request. Please try again.');
    }
  };

  const contactInfo = [
    {
      icon: MapPin,
      title: 'Visit Us',
      details: ['11A, Maharishi Debendra Road', 'Burrabazar, Kolkata', 'West Bengal, India - 700007, Room No. 308']
    },
    {
      icon: Phone,
      title: 'Call Us',
      details: ['+91 9007396826', '+91 9831170100', 'Mon-Sat: 9:00 AM - 6:00 PM']
    },
    {
      icon: Mail,
      title: 'Email Us',
      details: ['gaispatpvtltd@gmail.com', 'Sales & Support', 'Response within 24 hours']
    },
    {
      icon: Clock,
      title: 'Working Hours',
      details: ['Monday - Saturday', '9:00 AM - 6:00 PM', 'Sunday: Closed']
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="pt-32 pb-16 bg-gradient-to-br from-slate-50 to-slate-100">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl lg:text-6xl font-bold text-slate-900 mb-6">
              Get In <span className="text-orange-600">Touch</span>
            </h1>
            <p className="text-xl text-slate-600 leading-relaxed">
              Have questions or need a quote? Our team is here to help you with all your steel requirements
            </p>
          </div>
        </div>
      </section>

      {/* Contact Info Cards */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
            {contactInfo.map((info, index) => {
              const IconComponent = info.icon;
              return (
                <Card key={index} className="border-slate-200 hover:shadow-lg transition-all duration-300">
                  <CardContent className="p-6 text-center">
                    <div className="w-14 h-14 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <IconComponent className="w-7 h-7 text-orange-600" />
                    </div>
                    <h3 className="text-lg font-bold text-slate-900 mb-3">{info.title}</h3>
                    <div className="space-y-1">
                      {info.details.map((detail, idx) => (
                        <p key={idx} className="text-sm text-slate-600">{detail}</p>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Forms Section */}
      <section className="py-16 bg-slate-50">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Quote Request Form */}
            <Card className="border-slate-200 shadow-lg">
              <CardHeader className="bg-orange-600 text-white">
                <CardTitle className="text-2xl">Request a Quote</CardTitle>
                <p className="text-orange-100 text-sm">Get competitive pricing for your steel requirements</p>
              </CardHeader>
              <CardContent className="p-8">
                <form onSubmit={handleQuoteSubmit} className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="quote-name">Full Name *</Label>
                      <Input
                        id="quote-name"
                        value={quoteData.name}
                        onChange={(e) => setQuoteData({...quoteData, name: e.target.value})}
                        required
                        placeholder="John Doe"
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label htmlFor="quote-email">Email *</Label>
                      <Input
                        id="quote-email"
                        type="email"
                        value={quoteData.email}
                        onChange={(e) => setQuoteData({...quoteData, email: e.target.value})}
                        required
                        placeholder="john@example.com"
                        className="mt-1"
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="quote-phone">Phone Number *</Label>
                      <Input
                        id="quote-phone"
                        type="tel"
                        value={quoteData.phone}
                        onChange={(e) => setQuoteData({...quoteData, phone: e.target.value})}
                        required
                        placeholder="+91 98765 43210"
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label htmlFor="quote-company">Company Name</Label>
                      <Input
                        id="quote-company"
                        value={quoteData.company}
                        onChange={(e) => setQuoteData({...quoteData, company: e.target.value})}
                        placeholder="Company Ltd."
                        className="mt-1"
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="quote-product">Product Type *</Label>
                    <Select value={quoteData.product} onValueChange={(value) => setQuoteData({...quoteData, product: value})}>
                      <SelectTrigger className="mt-1">
                        <SelectValue placeholder="Select product type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="tmt-bars">TMT Bars</SelectItem>
                        <SelectItem value="round-bars">Round Bars</SelectItem>
                        <SelectItem value="flat-bars">Flat Bars</SelectItem>
                        <SelectItem value="structural-steel">Structural Steel</SelectItem>
                        <SelectItem value="steel-sheets">Steel Sheets & Plates</SelectItem>
                        <SelectItem value="steel-coils">Steel Coils</SelectItem>
                        <SelectItem value="pipes-tubes">Pipes & Tubes</SelectItem>
                        <SelectItem value="angles-channels">Angles & Channels</SelectItem>
                        <SelectItem value="other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="quote-quantity">Quantity (in tonnes) *</Label>
                      <Input
                        id="quote-quantity"
                        type="number"
                        value={quoteData.quantity}
                        onChange={(e) => setQuoteData({...quoteData, quantity: e.target.value})}
                        required
                        placeholder="100"
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label htmlFor="quote-location">Delivery Location *</Label>
                      <Input
                        id="quote-location"
                        value={quoteData.deliveryLocation}
                        onChange={(e) => setQuoteData({...quoteData, deliveryLocation: e.target.value})}
                        required
                        placeholder="City, State"
                        className="mt-1"
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="quote-requirements">Specific Requirements</Label>
                    <Textarea
                      id="quote-requirements"
                      value={quoteData.requirements}
                      onChange={(e) => setQuoteData({...quoteData, requirements: e.target.value})}
                      placeholder="Please specify grades, sizes, or any other requirements..."
                      rows={4}
                      className="mt-1"
                    />
                  </div>

                  <Button type="submit" className="w-full bg-orange-600 hover:bg-orange-700 text-white py-6 text-lg">
                    <Send className="mr-2 w-5 h-5" />
                    Request Quote
                  </Button>
                </form>
              </CardContent>
            </Card>

            {/* Contact Form */}
            <Card className="border-slate-200 shadow-lg">
              <CardHeader className="bg-slate-800 text-white">
                <CardTitle className="text-2xl">Send Us a Message</CardTitle>
                <p className="text-slate-300 text-sm">Have questions? We're here to help</p>
              </CardHeader>
              <CardContent className="p-8">
                <form onSubmit={handleContactSubmit} className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="contact-name">Full Name *</Label>
                      <Input
                        id="contact-name"
                        value={formData.name}
                        onChange={(e) => setFormData({...formData, name: e.target.value})}
                        required
                        placeholder="John Doe"
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label htmlFor="contact-email">Email *</Label>
                      <Input
                        id="contact-email"
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData({...formData, email: e.target.value})}
                        required
                        placeholder="john@example.com"
                        className="mt-1"
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="contact-phone">Phone Number</Label>
                      <Input
                        id="contact-phone"
                        type="tel"
                        value={formData.phone}
                        onChange={(e) => setFormData({...formData, phone: e.target.value})}
                        placeholder="+91 98765 43210"
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label htmlFor="contact-company">Company Name</Label>
                      <Input
                        id="contact-company"
                        value={formData.company}
                        onChange={(e) => setFormData({...formData, company: e.target.value})}
                        placeholder="Company Ltd."
                        className="mt-1"
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="contact-product">Product Interest</Label>
                    <Select value={formData.productType} onValueChange={(value) => setFormData({...formData, productType: value})}>
                      <SelectTrigger className="mt-1">
                        <SelectValue placeholder="Select product type (optional)" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="tmt-bars">TMT Bars</SelectItem>
                        <SelectItem value="structural-steel">Structural Steel</SelectItem>
                        <SelectItem value="steel-sheets">Steel Sheets & Plates</SelectItem>
                        <SelectItem value="steel-coils">Steel Coils</SelectItem>
                        <SelectItem value="pipes-tubes">Pipes & Tubes</SelectItem>
                        <SelectItem value="angles-channels">Angles & Channels</SelectItem>
                        <SelectItem value="general-inquiry">General Inquiry</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label htmlFor="contact-message">Message *</Label>
                    <Textarea
                      id="contact-message"
                      value={formData.message}
                      onChange={(e) => setFormData({...formData, message: e.target.value})}
                      required
                      placeholder="Tell us about your requirements..."
                      rows={6}
                      className="mt-1"
                    />
                  </div>

                  <Button type="submit" className="w-full bg-slate-800 hover:bg-slate-900 text-white py-6 text-lg">
                    <Send className="mr-2 w-5 h-5" />
                    Send Message
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-slate-900 mb-4">Visit Our Office</h2>
            <p className="text-xl text-slate-600">
              Located in the heart of Kolkata
            </p>
          </div>
          <div className="max-w-6xl mx-auto">
            <div className="rounded-2xl overflow-hidden shadow-xl" style={{ height: '450px' }}>
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3684.1!2d88.348!3d22.571!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMjLCsDM0JzE1LjYiTiA4OMKwMjAnNTIuOCJF!5e0!3m2!1sen!2sin!4v1640000000000!5m2!1sen!2sin&q=11A+Maharshi+Debendra+Road+Kolkata+700007"
                width="100%"
                height="450"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="G.A ISPAT Office - 11A Maharshi Debendra Road, Kolkata 700007"
              ></iframe>
            </div>
            <div className="mt-8 text-center bg-slate-50 rounded-xl p-6">
              <div className="flex items-center justify-center gap-2 text-slate-700 mb-4">
                <MapPin className="w-5 h-5 text-orange-600" />
                <p className="text-lg font-medium">
                  11A, Maharshi Debendra Road, Kolkata - 700007, Room No. 308
                </p>
              </div>
              <a 
                href="https://www.google.com/maps/dir/?api=1&destination=11A+Maharshi+Debendra+Road+Kolkata+700007" 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-6 py-3 bg-orange-600 hover:bg-orange-700 text-white font-semibold rounded-lg transition-colors"
              >
                <MapPin className="w-4 h-4" />
                Get Directions
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
